/**
 * Survey API Client
 * 與 POS 後端的 Survey API 進行通訊
 */

import { getApiBase, getApiHeaders, getTenantIdFromUrl } from './apiUtils';

/**
 * 動態問卷提交資料（新格式）
 */
interface DynamicSurveyData {
  templateId: number;
  memberId: string;
  phone: string;
  responseData: Record<string, any>;
  lineUserId?: string;
  displayName?: string;
}

/**
 * 舊版固定欄位格式（向後兼容）
 */
interface LegacySurveyData {
  memberId: string;
  phone: string;
  age: string;
  gender: string;
  location?: string;
  purchaseFrequency?: string;
  purchaseLocation?: string[];
  purchaseTime?: string;
  mealType?: string;
  purchaseFactors?: string[];
  healthPrice?: string;
  naturalPreference?: string;
  tastePreference?: string[];
  breadTypes?: string[];
  breadTypesOther?: string;
  favoriteBread?: string;
  desiredBread?: string;
  lineUserId?: string;
  displayName?: string;
}

type SurveyData = DynamicSurveyData | LegacySurveyData;

/**
 * 判斷是否為動態問卷格式
 */
function isDynamicSurvey(data: SurveyData): data is DynamicSurveyData {
  return 'templateId' in data && 'responseData' in data;
}

/**
 * 提交問卷資料
 */
export async function submitSurvey(data: SurveyData): Promise<unknown> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  let requestBody: Record<string, unknown>;

  if (isDynamicSurvey(data)) {
    // 動態問卷格式；memberId = customer UUID，phone 選填（僅在有效時傳送）
    requestBody = {
      templateId: data.templateId,
      memberId: data.memberId,
      responseData: data.responseData,
      lineUserId: data.lineUserId,
      displayName: data.displayName,
      ...(data.phone && /^09\d{8}$/.test(data.phone)
        ? { phone: data.phone }
        : {}),
    };
  } else {
    // 舊版固定欄位格式：向後兼容
    const cleanedData: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // 跳過空字串（enum 欄位不接受空字串）
      if (value === '') continue;
      // 跳過空陣列
      if (Array.isArray(value) && value.length === 0) continue;
      // 保留其他值
      cleanedData[key] = value;
    }

    requestBody = cleanedData;
  }

  console.log('📤 提交問卷:', {
    apiBase,
    data: requestBody,
    tenantId,
    isDynamic: isDynamicSurvey(data),
  });

  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/surveys`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    let errorMessage = '提交失敗，請稍後再試';
    let errorDetails: Array<{ field: string; message: string }> | undefined;
    try {
      const error = await response.json();
      console.error('❌ 提交失敗:', error);

      // 處理 Zod 驗證錯誤
      if (error.error && typeof error.error === 'object') {
        // 處理 ZodError 格式：error.error.message 包含 JSON 字串
        const zodError = error.error;
        if (zodError.name === 'ZodError' && zodError.message) {
          try {
            // ZodError 的 message 是 JSON 字串，需要解析
            const zodIssues = JSON.parse(zodError.message);
            if (Array.isArray(zodIssues)) {
              const details = zodIssues.map((issue: any) => {
                const field = Array.isArray(issue.path)
                  ? issue.path[0]
                  : issue.path || 'unknown';
                const message = issue.message || '驗證失敗';
                return { field, message };
              });
              errorDetails = details;

              // 構建詳細的錯誤訊息
              const fieldErrors = details.map(
                (d: { field: string; message: string }) => {
                  // 將字段名轉換為更友好的顯示名稱
                  let fieldName = d.field;
                  if (fieldName === 'memberId') fieldName = '會員手機號碼';
                  else if (fieldName === 'phone') fieldName = '手機號碼';

                  return `${fieldName}：${d.message}`;
                }
              );

              errorMessage = `請修正以下錯誤：\n${fieldErrors.join('\n')}`;
            }
          } catch (parseError) {
            // 如果解析失敗，使用原始錯誤訊息
            console.error('解析 ZodError 失敗:', parseError);
            errorMessage = zodError.message || errorMessage;
          }
        }
      }

      // 處理其他錯誤格式（包含 details 字段的舊格式）
      if (!errorDetails && error.details && Array.isArray(error.details)) {
        const details = error.details.map(
          (detail: { field: string; message: string; code?: string }) => ({
            field: detail.field,
            message: detail.message,
          })
        );
        errorDetails = details;

        // 構建詳細的錯誤訊息
        const fieldErrors = details.map(
          (d: { field: string; message: string }) => {
            // 將字段名轉換為更友好的顯示名稱
            let fieldName = d.field;
            if (fieldName === 'memberId') fieldName = '會員手機號碼';
            else if (fieldName === 'phone') fieldName = '手機號碼';

            return `${fieldName}：${d.message}`;
          }
        );

        errorMessage = `請修正以下錯誤：\n${fieldErrors.join('\n')}`;
      } else if (!errorDetails) {
        // 處理其他錯誤格式
        errorMessage = error.error?.message || error.message || errorMessage;
      }
    } catch {
      // 如果無法解析 JSON，使用狀態碼訊息
      console.error(
        '❌ 提交失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `提交失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }

    const error = new Error(errorMessage);
    (error as any).details = errorDetails; // 附加 details 供組件使用
    throw error;
  }

  const result = await response.json();
  console.log('✅ 提交成功:', result);
  return result;
}

/**
 * 查詢問卷資料（根據手機號碼）
 */
export async function getSurvey(memberId: string): Promise<unknown> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(
    `${apiBase}/api/surveys/by-phone/${memberId}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('查詢失敗');
  }

  return response.json();
}
